import { useEffect, useRef } from "react";
import { useAuthStore } from "../stores/auth.store";
import { getVapidPublicKey, subscribePush } from "../api/notification.api";

/**
 * Automatically subscribes the user to push notifications
 * when they are logged in and the browser supports it.
 * Runs once per session (or when user changes).
 */
export function usePushNotifications() {
  const user = useAuthStore((s) => s.user);
  const attemptedRef = useRef(false);

  useEffect(() => {
    if (!user || attemptedRef.current) return;
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;

    attemptedRef.current = true;

    (async () => {
      try {
        const registration = await navigator.serviceWorker.ready;

        // Check if already subscribed
        const existing = await registration.pushManager.getSubscription();
        if (existing) {
          // Re-register with backend (handles endpoint changes)
          await subscribePush(existing).catch(() => {});
          return;
        }

        // Ask for permission
        const permission = await Notification.requestPermission();
        if (permission !== "granted") return;

        // Get VAPID key from server
        const { publicKey } = await getVapidPublicKey();
        const applicationServerKey = urlBase64ToUint8Array(publicKey);

        // Subscribe
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: applicationServerKey.buffer as ArrayBuffer,
        });

        // Send subscription to backend
        await subscribePush(subscription);
      } catch {
        // Push setup is non-critical
      }
    })();
  }, [user]);
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
